import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { VehicleTypeProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vehicletype  } from "assets/SideBarIcons/vehicle-type.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import { GetVehicleTypeByIdResponse } from "@subModule/src/services/vehicletype/interface";
import Loader from "layout/Loader";
import { fetchMediaAsDataUrl } from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData { [key: string]: any;}
export default function VehicleTypeModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { register, control, handleSubmit, formState: { errors, dirtyFields, isDirty }, reset, watch } = useForm<FormData>({defaultValues: {}});
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
    const isArabicLanguage = (lang?: string) => {
      const l = (lang || "").toLowerCase();
      return l === "arabic" || l === "ar" || l.includes("arab");
    };
    const localizedVehicleTypePlaceholder = (lang?: string) =>
      isArabicLanguage(lang) ? t("phVehicleTypeAr") : t("phVehicleTypeEn");
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "vehicles",
        moduleLabelKey: "vehicleTypes",
        moduleHref: "/vehicle-types",
        entityFallbackLabelKey: "title",
        isEdit: !!params?.id,
    });
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.id ? false : true);
    const [isItdefault, setIsItDefault] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [imagePreview, setImagePreview] = useState<string>('');
    useEffect(() => {
        if(params?.id) {
            if(loading) getByID();
        }
    }, [params]);
    useEffect(() => {
        if (isDirty && params?.id) setIsBtnEnabled(true);
      }, [isDirty, dirtyFields]);
    const getByID = () => {
        VehicleTypeProfileService.getVehicleTypeByID(+(params as any)?.id).then(async (res: GetVehicleTypeByIdResponse) => {
            if(res.success) {
                captureLabel(res?.data?.title);
                reset({ ...(res?.data || {}), isDefault: res?.data?.default });
                setIsItDefault(res?.data?.default || false);
                const preview = await fetchMediaAsDataUrl((res?.data as any)?.image || '');
                setImagePreview(preview);
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if (!data.file) delete data.file;
        if (data.file && !data.file.type.startsWith('image/')) {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t('onlyImagesAllowed')} />, { variant: 'error' });
            return;
        }
        if (data.file && data.file.size > 10 * 1024 * 1024) {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t('fileSmaller10MB')} />, { variant: 'error' });
            return;
        }
        if(params?.id) {
            delete data.default;
            const imgUrl = data?.image || '';
            let imgFileName = '';
            if (imgUrl) {
                try {
                    imgFileName = new URL(imgUrl).pathname.split('/').pop() || '';
                } catch {
                    imgFileName = imgUrl.split('?')[0].split('/').pop() || '';
                }
                delete data.image;
            }
            VehicleTypeProfileService.updateVehicleType({
                ...data, 
                multiplier: +data.multiplier,
                oldUrl: imgFileName || ''
            }).then((response: any) => {
                if(response.success) {
                    captureLabel(data?.title);
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('vehicleType') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    enqueueSnackbar(<AlertMessages text={response?.message} />, { variant: 'error' });
                }
                setIsBtnEnabled(true);
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        } else {
            VehicleTypeProfileService.createVehicleType({...data, multiplier: +data.multiplier}).then((response: any) => {
                if(response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('vehicleType') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={response?.message} />, { variant: 'error' });
                }
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        }
    };

    return (<>
        <ComponentHeaderTwo
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate(-1)}
            onSubmit={handleSubmit(onSubmit)}
            heading={params?.id ? t('editVehicleType') : t('addVehicleType')}
            icon={<Vehicletype height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_VEHICLETYPE_CREATE")}
            breadcrumbItems={breadcrumbItems}
        />
        {loading ? <Loader /> : <SimpleModal
            visable={true}
            title={params?.id ? t('editVehicleType') : t('addVehicleType')}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={ 
                <Grid container component="form" id="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("title", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })} 
                            label={`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`}
                            placeholder={localizedVehicleTypePlaceholder(primaryLanguage)}
                            title={(`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.title} 
                            helperText={errors.title?.message as string} 
                            InputLabelProps={{ shrink: true }}
                            disabled={isItdefault as boolean}
                            sx={{ '& .Mui-disabled': { opacity: 0.7 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("secondaryTitle")} 
                            label={`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`}
                            placeholder={localizedVehicleTypePlaceholder(secondaryLanguage)}
                            title={(`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.secondaryTitle} 
                            helperText={errors.secondaryTitle?.message as string} 
                            InputLabelProps={{ shrink: true }}
                            disabled={isItdefault as boolean}
                            sx={{ '& .Mui-disabled': { opacity: 0.7 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            type="number"
                            {...register("multiplier", { 
                                required: t("This field is required."), 
                                validate: value => value > 0 || t('amountGreaterThanZero')
                            })}
                            label={t("servicePriceMultiplier") + ' *'}
                            placeholder={t("phServicePriceMultiplier")}
                            title={t("servicePriceMultiplier")}
                            fullWidth
                            error={!!errors.multiplier}
                            helperText={errors.multiplier?.message as string}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} id="importfile">
                        <Controller
                            name="file"
                            control={control}
                            rules={{ required: false }}
                                render={({ field }) => (<>
                                    <p className='font-Saira text-[13px] leading-[20px] text-[#343434] font-semibold text-start'>{t('Image')}</p>
                                    <input
                                        type="file"
                                        required
                                        accept="image/*"
                                        style={{ color: fileName ? 'inherit' : 'transparent' }}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            const file = event.target.files?.[0];
                                            setFileName(file ? file.name : '');
                                            field.onChange(file);
                                        }}
                                        title={fileName}
                                    />
                                </>)}
                        />
                        <MediaImage src={imagePreview} />
                    </Grid>
                </Grid>
            }
        />}
    </>)
}